export interface ExportPreviewFraming {
  pan: { x: number; y: number };
  zoom: number;
  previewSize: { width: number; height: number };
}

export interface CroppedExportPreviewImage {
  dataUrl: string;
  width: number;
  height: number;
}

const isDefaultFraming = ({ pan, zoom }: ExportPreviewFraming) =>
  zoom === 1 && pan.x === 0 && pan.y === 0;

/**
 * Crops a full export preview snapshot to match the pan/zoom framing shown in the modal.
 * Preview CSS applies `translate(pan) scale(zoom)` around the center of the preview box.
 */
export const cropExportPreviewImage = async (
  sourceDataUrl: string,
  framing: ExportPreviewFraming,
): Promise<CroppedExportPreviewImage> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { pan, zoom, previewSize } = framing;

      if (isDefaultFraming(framing)) {
        resolve({
          dataUrl: sourceDataUrl,
          width: img.width,
          height: img.height,
        });
        return;
      }

      const previewWidth = previewSize.width;
      const previewHeight = previewSize.height;
      const cropX = previewWidth / 2 - (previewWidth / 2 + pan.x) / zoom;
      const cropY = previewHeight / 2 - (previewHeight / 2 + pan.y) / zoom;
      const cropWidth = previewWidth / zoom;
      const cropHeight = previewHeight / zoom;

      const scaleX = img.width / previewWidth;
      const scaleY = img.height / previewHeight;

      const sourceX = cropX * scaleX;
      const sourceY = cropY * scaleY;
      const sourceWidth = cropWidth * scaleX;
      const sourceHeight = cropHeight * scaleY;

      const outputWidth = Math.max(1, Math.round(sourceWidth));
      const outputHeight = Math.max(1, Math.round(sourceHeight));

      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      context.fillStyle = "white";
      context.fillRect(0, 0, outputWidth, outputHeight);

      const clampedSourceX = Math.max(0, sourceX);
      const clampedSourceY = Math.max(0, sourceY);
      const clampedSourceWidth = Math.min(
        img.width - clampedSourceX,
        sourceWidth - (clampedSourceX - sourceX),
      );
      const clampedSourceHeight = Math.min(
        img.height - clampedSourceY,
        sourceHeight - (clampedSourceY - sourceY),
      );

      if (clampedSourceWidth > 0 && clampedSourceHeight > 0) {
        const destinationX =
          clampedSourceX > sourceX
            ? ((clampedSourceX - sourceX) / sourceWidth) * outputWidth
            : 0;
        const destinationY =
          clampedSourceY > sourceY
            ? ((clampedSourceY - sourceY) / sourceHeight) * outputHeight
            : 0;
        const destinationWidth = (clampedSourceWidth / sourceWidth) * outputWidth;
        const destinationHeight = (clampedSourceHeight / sourceHeight) * outputHeight;

        context.drawImage(
          img,
          clampedSourceX,
          clampedSourceY,
          clampedSourceWidth,
          clampedSourceHeight,
          destinationX,
          destinationY,
          destinationWidth,
          destinationHeight,
        );
      }

      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: outputWidth,
        height: outputHeight,
      });
    };
    img.onerror = () => reject(new Error("Failed to load preview image"));
    img.src = sourceDataUrl;
  });
